import { fromJS, getIn, List, Record, RecordOf } from 'immutable';
import { get } from 'lodash';

// CONSTANTS
import { AGREEMENT_ACTION_NOTIFICATIONS, AGREEMENT_ACTIONS } from 'constants/agreements';
import { ENTITY_IDS_BY_NAME } from 'constants/common';
import { DEFAULT_OFFSET, DEFAULT_PER_PAGE } from 'constants/table';

import { convertChargeToNegative, sanitizeIncomingAgreement } from 'views/Agreement/utils';
import { sanitizeIncomingContactlessAgreement } from 'views/Contactless/Contactless.utils';

import {
  ADD_DAMAGE_FAIL,
  ADD_DAMAGE_PICTURE_FAIL,
  ADD_FILTER,
  AGREEMENT_MANUAL_NOTIFIED_FAIL,
  AGREEMENT_MANUAL_NOTIFIED_SUCCESS,
  AGREEMENT_NOTIFICATION_FAIL,
  AGREEMENT_NOTIFICATION_SUCCESS,
  CHANGE_PER_PAGE_VALUE,
  CHARGE_AGREEMENT_CUSTOMER_FAIL,
  CHARGE_AGREEMENT_CUSTOMER_SUCCESS,
  CLEAR_AGREEMENT,
  CLEAR_FILTERS,
  CLEAR_NOTIFICATIONS,
  CLEAR_VEHICLE,
  CREATE_AGREEMENT_CHARGE_FAIL,
  CREATE_AGREEMENT_CHARGE_SUCCESS,
  CREATE_AGREEMENT_FAIL,
  CREATE_AGREEMENT_SUCCESS,
  DELETE_AGREEMENT_CHARGE_FAIL,
  DELETE_AGREEMENT_CHARGE_SUCCESS,
  DELETE_AGREEMENT_FAIL,
  DELETE_AGREEMENT_SUCCESS,
  DELETE_DAMAGE_FAIL,
  DELETE_DAMAGE_PICTURE_FAIL,
  GET_AGREEMENT_CHARGE_FAIL,
  GET_AGREEMENT_CHARGE_SUCCESS,
  GET_AGREEMENT_FAIL,
  GET_AGREEMENT_HTML,
  GET_AGREEMENT_SUCCESS,
  GET_AGREEMENTS_FAIL,
  GET_AGREEMENTS_SUCCESS,
  GET_CONTACTLESS_AGREEMENT_FAIL,
  GET_CONTACTLESS_AGREEMENT_SUCCESS,
  LIST_AGREEMENT_CHARGE_FAIL,
  LIST_AGREEMENT_CHARGE_SUCCESS,
  MARK_AS_PAID_AGREEMENT_CUSTOMER_SUCCESS,
  MARK_AS_PAID_AGREEMENT_CUSTOMER_FAIL,
  PREVIEW_AGREEMENT_CHARGE_CALCULATIONS_FAIL,
  PREVIEW_AGREEMENT_CHARGE_CALCULATIONS_SUCCESS,
  REFRESH_FIELD_FAIL,
  REFRESH_FIELD_SUCCESS,
  REFRESH_FUEL_SUCCESS,
  REFRESH_ODOMETER_SUCCESS,
  REMOVE_FILTER,
  RESET_AGREEMENTS_DATA,
  SELECT_VIEW,
  SET_FILTERS,
  SEND_COPY_FAIL,
  SEND_COPY_SUCCESS,
  SEND_FOR_SIGNATURE_FAIL,
  SEND_FOR_SIGNATURE_SUCCESS,
  SET_AGREEMENT,
  SET_AGREEMENTS_OFFSET,
  SET_COLUMNS,
  SET_INSPECTION_IN,
  SET_INSPECTION_OUT,
  SET_SEARCH_QUERY,
  SET_VEHICLE,
  START_AGREEMENTS_REQUEST,
  START_LOADING,
  STOP_LOADING,
  UPDATE_AGREEMENT_CHARGE_FAIL,
  UPDATE_AGREEMENT_CHARGE_SUCCESS,
  UPDATE_AGREEMENT_FAIL,
  UPDATE_AGREEMENT_SUCCESS,
  UPDATE_AGREEMENT_SUCCESS_SILENT,
  UPDATE_CC_FAIL,
  UPDATE_CC_SUCCESS,
  UPDATE_DAMAGE_FAIL,
  UPDATE_SIGNATURE_FAIL,
  UPDATE_SIGNATURE_SUCCESS,
  GET_GEOFENCE_ALERTS_FAIL,
  GET_GEOFENCE_ALERTS_SUCCESS,
  CLEAR_VIOLATIONS,
  GET_AGREEMENT_VEHICLE_SUCCESS,
  GET_AGREEMENT_VEHICLE_FAIL,
} from './actions';

import { UPDATE_VEHICLE_DEPARTMENT_SUCCESS, UPDATE_VEHICLE_SUCCESS } from 'services/Vehicles/actions';
import {
  CREATE_VIEW_SUCCESS,
  DELETE_VIEW_SUCCESS,
  GET_USER_VIEWS_SUCCESS,
  UPDATE_VIEW_SUCCESS,
} from 'services/Views/actions';

import { CLEAR_CHARGES } from 'services/FleetSettings/actions';

import { GENERIC_ERROR } from 'constants/errors.constants';
import { AgreementRecordFactory, ReduxAgreementRecordFactory } from './factories';
import { Agreement, AgreementCharge, ReduxAgreement, GeofenceAlertType } from 'services/resources';
import { withLatestRequest } from 'services/latestRequest';
import { DISCOUNTS_VALUES } from 'views/FleetSettings/Charges/components';

type TranslationObject = {
  translationId: string;
};

const AgreementsStateFactory = Record({
  views: undefined,
  activeView: undefined,
  filters: List(),
  columns: List(),

  agreements: List<List<RecordOf<Agreement>>>(),

  calendarAgreements: List(),

  calculatedCharges: null,
  perPage: DEFAULT_PER_PAGE,
  offset: DEFAULT_OFFSET,
  total: 0,

  searchQuery: undefined,

  agreement: ReduxAgreementRecordFactory({}),
  vehicle: {},
  violations: List<GeofenceAlertType>(),

  html: '',

  notifications: List<TranslationObject>(),
  loading: false,
  requestId: 0,
  errors: Record<any>({})(),
});

const initialState = AgreementsStateFactory({});

function agreementsServiceReducer(state = initialState, action) {
  switch (action.type) {
    case ADD_FILTER:
      return state
        .set('loading', false)
        .update('filters', (filters) => filters.push(fromJS(action.payload.filter)))
        .set('agreements', List())
        .set('offset', DEFAULT_OFFSET)
        .set('total', 0);
    case AGREEMENT_MANUAL_NOTIFIED_SUCCESS: {
      const notifications = [
        {
          translationId: 'views.Agreement.notifications.manual.success',
        },
      ];

      return state.set('loading', false).set('notifications', List(notifications)).set('errors', Record({})());
    }
    case AGREEMENT_NOTIFICATION_FAIL: {
      const message = {
        translationId: 'views.Agreement.notifications.failureMessage',
      };

      const errorDetail = getIn(action, ['payload', 'errors', 'data', 'detail']);

      if (errorDetail === 'Bad credentials') {
        return state.set('loading', false).set(
          'errors',
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          fromJS({
            message: {
              translationId: 'views.Agreement.notifications.badCredentials',
            },
          })
        );
      }
      // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
      return state.set('loading', false).set('errors', fromJS({ message }));
    }
    case AGREEMENT_NOTIFICATION_SUCCESS: {
      const notifications = [
        {
          translationId: 'views.Agreement.notifications.sms.success',
        },
      ];

      // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
      return state.set('loading', false).set('notifications', fromJS(notifications)).set('errors', Record({})());
    }
    case CHANGE_PER_PAGE_VALUE:
      return state
        .set('loading', false)
        .set('agreements', List())
        .set('perPage', action.payload.perPage)
        .set('offset', DEFAULT_OFFSET)
        .set('total', 0);
    case CHARGE_AGREEMENT_CUSTOMER_SUCCESS: {
      const agreement = sanitizeIncomingAgreement(action.payload.agreement);
      return state
        .set('loading', false)
        .set('agreement', agreement)
        .set('calculatedCharges', fromJS(agreement.get('charges') || fromJS([])))
        .set('notifications', List([{ translationId: 'services.Agreements.cc.charge.success' }]))
        .set('errors', Record({})());
    }
    case MARK_AS_PAID_AGREEMENT_CUSTOMER_SUCCESS: {
      const agreement = sanitizeIncomingAgreement(action.payload.agreement);
      return state
        .set('loading', false)
        .set('agreement', agreement)
        .set('calculatedCharges', fromJS(agreement.get('charges') || fromJS([])))
        .set('notifications', List([{ translationId: 'services.Agreements.markedAsPaid.success' }]))
        .set('errors', Record({})());
    }
    case CLEAR_AGREEMENT: {
      return state.set('agreement', ReduxAgreementRecordFactory({}));
    }
    case CLEAR_CHARGES:
      return state.set('calculatedCharges', null);
    case CLEAR_FILTERS:
      return state
        .set('loading', false)
        .set('filters', state.get('activeView') ? state.get('activeView').get('rule') : fromJS([]))
        .set('agreements', List())
        .set('offset', DEFAULT_OFFSET)
        .set('total', 0);
    case CLEAR_NOTIFICATIONS:
      // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
      return state.set('errors', fromJS({})).set('notifications', List([]));
    case CLEAR_VEHICLE:
      return state.set('vehicle', fromJS({}));
    case CREATE_AGREEMENT_SUCCESS: {
      return state.set('loading', false).set('notifications', List([])).set('errors', Record({})());
    }
    case CREATE_VIEW_SUCCESS:
      if (action.payload.view.type === ENTITY_IDS_BY_NAME.agreements) {
        return state
          .set('loading', false)
          .set('views', state.get('views').push(fromJS(action.payload.view)))
          .set('activeView', fromJS(action.payload.view));
      }

      return state;
    case DELETE_AGREEMENT_SUCCESS:
      return state
        .set('loading', false)
        .set('agreement', ReduxAgreementRecordFactory({}))
        .set('notifications', List([{ translationId: 'views.Agreement.delete.success' }]))
        .set('errors', Record({})());
    case DELETE_VIEW_SUCCESS: {
      const index = state.get('views').findIndex((view) => view.get('id') === action.payload.id);

      if (index > -1) {
        return state
          .set('loading', false)
          .set('views', state.get('views').remove(index))
          .set('activeView', null)
          .set('filters', List())
          .set('columns', List());
      }

      return state;
    }
    case GET_AGREEMENT_HTML:
      return state.set('html', action.payload.html);
    case GET_AGREEMENT_SUCCESS: {
      const agreement = sanitizeIncomingAgreement(action.payload.agreement);
      return state
        .set('loading', false)
        .set('agreement', agreement)
        .set('calculatedCharges', agreement.get('charges') || fromJS([]))
        .set('errors', Record({})());
    }
    case GET_AGREEMENTS_FAIL:
      return state
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors.data))
        .set('agreements', List())
        .set('perPage', DEFAULT_PER_PAGE)
        .set('offset', DEFAULT_OFFSET)
        .set('total', 0);
    case GET_AGREEMENT_VEHICLE_SUCCESS: {
      // TODO: Create 'sanitizeIncomingVehicle' to display only the necessary fields
      const vehicle = action.payload.vehicle;
      return state.set('loading', false).set('vehicle', vehicle).set('errors', Record({})());
    }
    case GET_AGREEMENTS_SUCCESS: {
      const pageNumber = action.payload.offset / state.get('perPage') + 1;

      const newAgreementList = action.payload.data.results.map((agreement: Agreement) =>
        AgreementRecordFactory(agreement)
      ) as RecordOf<Agreement>[];

      const newCustomers = state.set('loading', false).get('agreements').set(pageNumber, List(newAgreementList));

      return state
        .set('loading', false)
        .set('agreements', newCustomers)
        .set('offset', action.payload.offset)
        .set('total', action.payload.data.count)
        .set('errors', Record({})());
    }
    case GET_CONTACTLESS_AGREEMENT_FAIL:
      return state
        .set('loading', false)
        .set('errors', fromJS(action.payload.errors.data))
        .set('agreement', ReduxAgreementRecordFactory({}));
    case GET_CONTACTLESS_AGREEMENT_SUCCESS: {
      const agreement = sanitizeIncomingContactlessAgreement(action.payload.agreement);
      return state
        .set('loading', false)
        .set('agreement', ReduxAgreementRecordFactory(agreement))
        .set('errors', Record({})());
    }
    case GET_USER_VIEWS_SUCCESS: {
      const agreementViews = fromJS(
        action.payload.views.results.filter((view) => view.type === ENTITY_IDS_BY_NAME.agreements)
      );
      const defaultView = agreementViews.find((view) => view.get('isDefault'));
      let activeView = state.get('activeView');
      let filters = state.get('filters');
      let columns = state.get('columns');

      if (!agreementViews.includes(activeView)) {
        activeView = null;
      }

      if (typeof defaultView !== 'undefined') {
        activeView = defaultView;
        filters = defaultView.get('rules');
        columns = defaultView.get('columns');
      }

      return state
        .set('loading', false)
        .set('views', agreementViews)
        .set('activeView', activeView)
        .set('filters', filters)
        .set('columns', columns);
    }
    case PREVIEW_AGREEMENT_CHARGE_CALCULATIONS_FAIL:
      return state;
    case PREVIEW_AGREEMENT_CHARGE_CALCULATIONS_SUCCESS: {
      return state;
    }
    case REFRESH_FIELD_SUCCESS: {
      const agreement = sanitizeIncomingAgreement(action.payload.agreement);
      return state.set('loading', false).set('agreement', ReduxAgreementRecordFactory(agreement));
    }
    case REFRESH_FUEL_SUCCESS:
      return state
        .setIn(['vehicle', 'isConnected'], action.payload.isConnected)
        .setIn(['vehicle', 'fuelLevel'], action.payload.fuelLevel)
        .setIn(['vehicle', 'connectedFuelLevel'], action.payload.connectedFuelLevel)
        .setIn(['vehicle', 'fuelLevelDate'], action.payload.fuelLevelDate)
        .setIn(['vehicle', 'fuelLevelStale'], action.payload.fuelLevelStale);
    case REFRESH_ODOMETER_SUCCESS:
      return state
        .setIn(['vehicle', 'isConnected'], action.payload.isConnected)
        .setIn(['vehicle', 'odometer'], action.payload.odometer)
        .setIn(['vehicle', 'connectedOdometer'], action.payload.connectedOdometer)
        .setIn(['vehicle', 'mileageCalculated'], action.payload.mileageCalculated)
        .setIn(['vehicle', 'mileageCalculatedDate'], action.payload.mileageCalculatedDate)
        .setIn(['vehicle', 'odometerInitial'], action.payload.odometerInitial)
        .setIn(['vehicle', 'odometerInitialDate'], action.payload.odometerInitialDate)
        .setIn(['vehicle', 'odometerStale'], action.payload.odometerStale);
    case REMOVE_FILTER:
      return state
        .set('loading', false)
        .set('filters', state.get('filters').delete(action.payload.index))
        .set('agreements', List())
        .set('offset', DEFAULT_OFFSET)
        .set('total', 0);
    case RESET_AGREEMENTS_DATA:
      return state.set('agreements', List());
    case SET_FILTERS:
      return state.set('filters', action.payload.filters);
    case SELECT_VIEW:
      return state
        .set('loading', false)
        .set('offset', DEFAULT_OFFSET)
        .set('activeView', action.payload.view)
        .set('filters', action.payload.view ? action.payload.view.get('rules') : fromJS([]))
        .set('columns', action.payload.view ? action.payload.view.get('columns') : fromJS([]));
    case SEND_COPY_FAIL:
      return state.set(
        'notifications',
        List([
          {
            translationId: 'views.ESignature.notifications.sendCopyFail',
          },
        ])
      );
    case SEND_COPY_SUCCESS:
      return state.set(
        'notifications',
        List([
          {
            translationId: 'views.ESignature.notifications.sendCopySuccess',
          },
        ])
      );
    case SEND_FOR_SIGNATURE_SUCCESS: {
      const agreement = sanitizeIncomingAgreement(action.payload.agreement);
      return state.set('loading', false).set('agreement', agreement);
    }
    case SET_AGREEMENT: {
      const agreement = sanitizeIncomingAgreement(action.payload.agreement);
      return state.set('loading', false).set('agreement', agreement);
    }
    case SET_AGREEMENTS_OFFSET:
      return state.set('offset', action.payload.offset);
    case SET_COLUMNS:
      return state.set('loading', false).set('columns', List(action.payload.columns));
    case SET_INSPECTION_OUT:
      // @ts-expect-error Argument of type '"inspectionOut"' is not assignable to parameter of type...
      return state.set('loading', false).set('inspectionOut', action.payload.inspectionOut);
    case SET_INSPECTION_IN:
      // @ts-expect-error Argument of type '"inspectionIn"' is not assignable to parameter of type...
      return state.set('loading', false).set('inspectionIn', action.payload.inspectionIn);
    case SET_SEARCH_QUERY:
      return state.set('offset', DEFAULT_OFFSET).set('searchQuery', fromJS(action.payload.searchQuery));
    case SET_VEHICLE:
      return state.set('vehicle', fromJS(action.payload.vehicle));
    case START_LOADING:
      return state.set('loading', true);
    case START_AGREEMENTS_REQUEST:
      return state.set('loading', true);
    case STOP_LOADING:
      return state.set('loading', false);
    case UPDATE_AGREEMENT_SUCCESS: {
      const agreement = sanitizeIncomingAgreement(action.payload.agreement);
      const actionType = action.payload.action || AGREEMENT_ACTIONS.SAVE;

      const notifications = List([
        {
          translationId: AGREEMENT_ACTION_NOTIFICATIONS[actionType],
        },
      ]);

      return state
        .set('loading', false)
        .set('agreement', agreement)
        .set('notifications', notifications)
        .set('errors', Record({})());
    }
    case UPDATE_AGREEMENT_SUCCESS_SILENT: {
      const agreement = sanitizeIncomingAgreement(action.payload.agreement);

      return state.set('loading', false).set('agreement', agreement).set('errors', Record({})());
    }
    case UPDATE_CC_FAIL: {
      let errors;

      const errorMessage = get(action.payload.errors, 'message');

      if (errorMessage) {
        errors = [errorMessage];
      } else {
        errors = action.payload.errors.data || GENERIC_ERROR;
      }

      return state.set('loading', false).set('errors', fromJS(errors));
    }
    case UPDATE_CC_SUCCESS: {
      const agreement = sanitizeIncomingAgreement(action.payload.agreement);

      return state
        .set('loading', false)
        .set('agreement', agreement)
        .set('notifications', List([{ translationId: 'services.Agreements.cc.update.success' }]))
        .set('errors', Record({})());
    }
    case UPDATE_SIGNATURE_SUCCESS:
      return state.set('loading', false);
    case UPDATE_VEHICLE_DEPARTMENT_SUCCESS:
      return state.set(
        'notifications',
        List([
          {
            translationId: 'notification.vehicleDepartmentUpdated',
          },
        ])
      );
    case UPDATE_VEHICLE_SUCCESS:
      return state.set(
        'notifications',
        List([
          {
            translationId: 'notification.vehicleInformationUpdated',
          },
        ])
      );
    case UPDATE_VIEW_SUCCESS: {
      if (action.payload.view.type === ENTITY_IDS_BY_NAME.agreements) {
        const index = state.get('views').findIndex((view) => view.get('id') === action.payload.view.id);
        const updatedView = fromJS(action.payload.view);

        return state
          .update('views', (views) => views.set(index, updatedView))
          .set('loading', false)
          .set('activeView', action.payload.resetDefault ? null : updatedView);
      }
      return state;
    }
    case CREATE_AGREEMENT_CHARGE_SUCCESS: {
      const currentAgreementRecord = state.get('agreement');
      const agreement = currentAgreementRecord.toJS() as ReduxAgreement;

      const incomingCharge = action.payload.charge as AgreementCharge;

      const newCharge = DISCOUNTS_VALUES.includes(incomingCharge.category)
        ? convertChargeToNegative(incomingCharge)
        : incomingCharge;

      return state.set(
        'agreement',
        ReduxAgreementRecordFactory({
          ...agreement,
          charges: [...currentAgreementRecord.charges, newCharge],
        })
      );
    }
    case DELETE_AGREEMENT_CHARGE_SUCCESS: {
      const currentAgreementRecord = state.get('agreement');
      const agreement = currentAgreementRecord.toJS() as ReduxAgreement;

      const oldChargeId = action.payload.chargeId;
      const updatedChargeList = agreement.charges.filter((selectedCharge) => selectedCharge.id !== oldChargeId);

      return state.set(
        'agreement',
        ReduxAgreementRecordFactory({
          ...agreement,
          charges: [...updatedChargeList],
        })
      );
    }
    case GET_GEOFENCE_ALERTS_SUCCESS: {
      const violationsList = action.payload.data.results as List<GeofenceAlertType>;
      return state.set('violations', violationsList).set('loading', false);
    }
    case CLEAR_VIOLATIONS: {
      return state.set('violations', List()).set('loading', false);
    }
    case UPDATE_AGREEMENT_CHARGE_SUCCESS:
      return state.set('loading', false);
    case GET_AGREEMENT_CHARGE_SUCCESS:
    case LIST_AGREEMENT_CHARGE_SUCCESS:
      throw Error('Not implemented');
    case CREATE_AGREEMENT_CHARGE_FAIL:
    case DELETE_AGREEMENT_CHARGE_FAIL:
    case GET_AGREEMENT_CHARGE_FAIL:
    case LIST_AGREEMENT_CHARGE_FAIL:
    case UPDATE_AGREEMENT_CHARGE_FAIL:
    case ADD_DAMAGE_FAIL:
    case UPDATE_DAMAGE_FAIL:
    case DELETE_DAMAGE_FAIL:
    case ADD_DAMAGE_PICTURE_FAIL:
    case DELETE_DAMAGE_PICTURE_FAIL:
    case AGREEMENT_MANUAL_NOTIFIED_FAIL:
    case CREATE_AGREEMENT_FAIL:
    case CHARGE_AGREEMENT_CUSTOMER_FAIL:
    case MARK_AS_PAID_AGREEMENT_CUSTOMER_FAIL:
    case DELETE_AGREEMENT_FAIL:
    case GET_AGREEMENT_FAIL:
    case GET_AGREEMENT_VEHICLE_FAIL:
    case REFRESH_FIELD_FAIL:
    case SEND_FOR_SIGNATURE_FAIL:
    case UPDATE_AGREEMENT_FAIL:
    case GET_GEOFENCE_ALERTS_FAIL:
    case UPDATE_SIGNATURE_FAIL: {
      return (
        state
          .set('loading', false)
          // @ts-expect-error: Argument of type 'Collection<unknown, unknown>' is not assignable...
          .set('errors', fromJS(getIn(action, ['payload', 'errors', 'data']) || GENERIC_ERROR))
      );
    }
    default:
      return state;
  }
}

export default withLatestRequest({
  start: START_AGREEMENTS_REQUEST,
  settle: [GET_AGREEMENTS_SUCCESS, GET_AGREEMENTS_FAIL],
  loadingKey: 'loading',
})(agreementsServiceReducer);
